import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Table from "@/components/ui/table";
import type { TableColumn } from "@/types/component";
import ReportDetailLayout from "../../components/ReportDetailLayout";
import ReportFilterBar, {
  type ReportFilterState,
} from "../../components/ReportFilterBar";
import ReportKPICard from "../../components/ReportKPICard";
import ReportChart from "../../components/ReportChart";
import ReportExportButtons from "../../components/ReportExportButtons";
import { useReportData } from "../../hooks/useReportData";
import { useReportConfig } from "../../hooks/useReportConfig";
import {
  reportsExtendedApi,
  type SurveySatisfactionResponse,
  type SurveySatisfactionItem,
  type SurveySatisfactionParams,
} from "../../services/reports-extended.api";
import { Heart, Question } from "@/components/icons";

const PAGE_SIZE = 20;

const SurveyNpsReportPage: React.FC = () => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<ReportFilterState>({});
  const [page, setPage] = useState(1);

  const params: SurveySatisfactionParams = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      dateFrom: filter.dateFrom,
      dateTo: filter.dateTo,
      search: filter.search?.trim() || undefined,
    }),
    [filter, page],
  );

  const { data, isLoading, error } = useReportData<
    SurveySatisfactionResponse,
    SurveySatisfactionParams
  >({
    fetcher: (p, signal) =>
      reportsExtendedApi.getSurveySatisfaction(p, signal),
    params,
  });

  const summary = data?.summary;
  const items = data?.items ?? [];
  const count = data?.count ?? 0;

  const topItems = items.slice(0, 10);

  const npsBadge = (score: number) => {
    const good = score >= 30;
    const bad = score < 0;
    const label = good
      ? t("reports.surveyNps.strong")
      : bad
        ? t("reports.surveyNps.weak")
        : score >= 0
          ? "Orta"
          : "—";
    const color = good ? "#15803d" : bad ? "#b91c1c" : "#b45309";
    const bg = good ? "#dcfce7" : bad ? "#fee2e2" : "#fef3c7";
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "2px 8px",
          borderRadius: 999,
          fontSize: 12,
          fontWeight: 600,
          color,
          background: bg,
        }}
      >
        {score.toFixed(0)} · {label}
      </span>
    );
  };

  const columns: TableColumn<SurveySatisfactionItem>[] = [
    { header: t("reports.surveyNps.headerTraining"), accessor: (row) => row.educationTitle },
    { header: "ANKET", accessor: (row) => row.surveyName ?? "—" },
    {
      key: "responseCount",
      label: "YANIT",
      header: "YANIT",
      accessor: (row) => row.responseCount,
    },
    {
      key: "uniqueRespondents",
      label: "YANITLAYAN",
      header: "YANITLAYAN",
      accessor: (row) => row.uniqueRespondents,
    },
    {
      key: "avgRating",
      label: "ORT. PUAN",
      header: "ORT. PUAN",
      accessor: (row) => row.avgRating.toFixed(2),
    },
    {
      key: "responseCount2",
      label: "NPS",
      header: "NPS",
      accessor: (row) =>
        row.responseCount > 0 ? npsBadge(row.npsScore) : "—",
    },
  ];

  // R11 yapılandırılabilirliği: kolon ayarları, kayıtlı filtre setleri,
  // zamanlanmış e-posta raporu. Blok düzenini ReportDetailLayout yönetir.
  const cfg = useReportConfig<SurveySatisfactionItem>({
    reportKey: "SURVEY_SATISFACTION",
    reportTitle: "Anket Memnuniyet (NPS/CSAT)",
    columns,
  });

  useEffect(() => {
    if (cfg.defaultFilters) setFilter(cfg.defaultFilters as ReportFilterState);
  }, [cfg.defaultFilters]);

  return (
    <ReportDetailLayout
      title="Anket Memnuniyet (NPS/CSAT)"
      layoutKey={`report:SURVEY_SATISFACTION`}
      presetSlot={cfg.presetBar(filter as Record<string, unknown>, (f) =>
        setFilter(f as ReportFilterState),
      )}
      modalSlot={cfg.scheduleModal(filter as Record<string, unknown>)}
      categoryLabel={t("reportCatalog.categories.quality.title")}
      description={t("reports.surveyNps.description")}
      exportSlot={
        <>
          {cfg.toolbar}
          <ReportExportButtons
          reportType="SURVEY_SATISFACTION"
          filters={params as unknown as Record<string, unknown>}
          filenameBase="anket-memnuniyet"
          exportFn={reportsExtendedApi.exportReport}
          disabled={isLoading}
        />
        </>
      }
      filterSlot={
        <ReportFilterBar
          value={filter}
          onChange={(next) => {
            setFilter(next);
            setPage(1);
          }}
          showDepartment={false}
        />
      }
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 14,
        }}
      >
        <ReportKPICard
          title={t("reports.surveyNps.totalResponses")}
          value={summary?.totalResponses ?? 0}
          icon={<Question width={18} height={18} />}
        />
        <ReportKPICard
          title={t("reports.surveyNps.respondedTraining")}
          value={summary?.respondedEducations ?? 0}
          icon={<Question width={18} height={18} />}
        />
        <ReportKPICard
          title="Ort. Puan (CSAT)"
          value={summary ? summary.avgRating.toFixed(2) : "—"}
          icon={<Heart width={18} height={18} />}
          accent="success"
        />
        <ReportKPICard
          title="Ort. NPS"
          value={summary ? summary.avgNps.toFixed(0) : "—"}
          icon={<Heart width={18} height={18} />}
          accent={
            summary && summary.avgNps >= 30
              ? "success"
              : summary && summary.avgNps < 0
                ? "danger"
                : "warning"
          }
        />
      </div>

      <ReportChart
        title={t("reports.surveyNps.top10")}
        type="bar"
        isLoading={isLoading}
        isEmpty={topItems.length === 0}
        series={[
          {
            name: "Ort. Puan",
            data: topItems.map((i) => i.avgRating),
          },
        ]}
        options={{
          xaxis: { categories: topItems.map((i) => i.educationTitle) },
          plotOptions: {
            bar: { horizontal: true, borderRadius: 4, barHeight: "70%" },
          },
          colors: ["#10b981"],
          yaxis: { max: 10, min: 0 },
        }}
      />

      {cfg.columnSettings}

      <Table
        columns={cfg.columns}
        data={items}
        isLoading={isLoading}
        getRowId={(row) => row.educationID}
        totalRecords={count}
        currentPage={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        emptyMessage={error ? t("reports.surveyNps.noData") : t("reports.surveyNps.noResponse")}
      />
    </ReportDetailLayout>
  );
};

export default SurveyNpsReportPage;
